import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Gauge, LogOut, ShieldCheck, Trash2, UserCheck, UserRound, X } from 'lucide-react';
import api, { errorText } from '../services/api';
import { useApi } from '../lib/useApi';
import { useAccess } from '../context/AuthContext';
import { actionLabel, ago, date, num } from '../lib/format';
import { Button, Card, Chip, Empty, ErrorBanner, Field, Input, Modal, PageHeader, PageSkeleton, SearchBox, Segmented, Stat, TenantLogo, Tone, useToast } from '../components/ui';

type Status = 'active' | 'unverified' | 'blocked';
interface Rating { score: number | null; risk_level: string | null; scanned_at: string | null }
interface Account {
  id: string; email: string; name: string | null; status: Status; created_at: string; last_login_at: string | null;
  company_domain: string | null; rating: Rating | null; blocked_reason: string | null;
  claimed_tenant_id: string | null; claimed_tenant_name: string | null;
  // The one customer whose invite actually brought this person to sign up (tenants.source_tenant_id,
  // resolved) -- a different, more specific fact than invited_by, which lists every customer who has
  // ever invited this email, not just the one that led to this signup.
  signed_up_via_tenant_id: string | null; signed_up_via_tenant_name: string | null;
  invited_by: { tenant_id: string; company_name: string; vendor_name: string | null }[];
}
interface Totals { accounts: number; new_30d: number; blocked: number; rated: number }
interface Detail {
  account: Account & { email_verified: boolean };
  invited_by: { tenant_id: string; company_name: string; vendor_name: string | null; vendor_domain: string | null }[];
  questionnaires: { id: string; status: string; sent_at: string | null; answered_at: string | null; tenant: string; vendor: string | null; questionnaire: string | null }[];
  remediation: { id: string; title: string; status: string; created_at: string; tenant: string; vendor: string | null }[];
  history: { action: string; details: string; created_at: string; actor: string | null }[];
}

const STATUS: Record<Status, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'green' }, unverified: { label: 'Email not verified', tone: 'amber' }, blocked: { label: 'Blocked', tone: 'red' },
};
const grade = (s: number | null) => (s == null ? null : s >= 90 ? 'A' : s >= 80 ? 'B' : s >= 70 ? 'C' : s >= 60 ? 'D' : 'F');
const GRADE_TONE: Record<string, Tone> = { A: 'green', B: 'teal', C: 'amber', D: 'orange', F: 'red' };

/** People who signed up for their own free FluxWatch workspace from the vendor portal's "get your
    rating" offer (claimed_tenant_id IS NOT NULL -- the backend already scopes /portal-accounts to
    this). Someone who was only invited to answer one customer's questionnaire and never claimed a
    workspace is between that customer and their own vendor, not a platform-level concern, so they
    never appear here at all. */
export default function PortalAccounts() {
  const [view, setView] = useState<'all' | Status>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useApi<{ accounts: Account[]; totals: Totals }>('/portal-accounts');

  const all = data?.accounts || [];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(a => {
      if (view !== 'all' && a.status !== view) return false;
      if (q && ![a.email, a.name, a.company_domain, a.signed_up_via_tenant_name, ...a.invited_by.map(i => i.company_name)].join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [all, view, search]);

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorBanner text={error} onRetry={reload} />;
  const t = data!.totals;

  return (
    <div>
      <PageHeader title="Portal Signups" subtitle="People who signed up for their own free FluxWatch workspace from the vendor portal's security rating offer." />

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Signed up" value={num(t.accounts)} sub={`${num(t.new_30d)} new in 30 days`} icon={UserCheck} tone="green" />
        <Stat label="Shown their rating first" value={num(t.rated)} sub="rated by FluxWatch before signing up" icon={Gauge} tone="purple" />
        <Stat label="Blocked" value={num(t.blocked)} sub="can't sign in to the portal" icon={Ban} tone="red" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented value={view} onChange={setView} options={[
          { key: 'all', label: 'All', count: all.length },
          { key: 'active', label: 'Active', count: all.filter(a => a.status === 'active').length },
          { key: 'unverified', label: 'Not verified', count: all.filter(a => a.status === 'unverified').length },
          { key: 'blocked', label: 'Blocked', count: all.filter(a => a.status === 'blocked').length },
        ]} />
        <div className="ml-auto"><SearchBox value={search} onChange={setSearch} placeholder="Search name, email, company or customer" /></div>
      </div>

      <Card bodyClass="p-0">
        {rows.length === 0 ? (
          <Empty icon={UserRound} title={all.length ? 'No accounts match' : 'No one has signed up yet'}
            text={all.length ? 'Try a different view or search.' : 'They appear here once they claim their own free workspace from the vendor portal.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b border-slate-100 bg-slate-50/60">
                <tr>{['Person', 'Company', 'Rating', 'Signed up via', 'Status', 'Last sign-in', 'Joined'].map(h =>
                  <th key={h} className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(a => {
                  const g = grade(a.rating?.score ?? null);
                  return (
                    <tr key={a.id} onClick={() => setOpen(a.id)} className="cursor-pointer hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-slate-900">{a.name || a.email.split('@')[0]}</div>
                        <div className="text-xs text-slate-500">{a.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <TenantLogo name={a.company_domain || a.email} domain={a.company_domain} size={24} />
                          <span className="text-sm text-slate-700">{a.company_domain || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{a.rating?.score != null ? <Chip tone={GRADE_TONE[g || ''] || 'slate'}>{a.rating.score} · {g}</Chip> : <span className="text-xs text-slate-400">—</span>}</td>
                      <td className="max-w-[220px] px-4 py-3 text-sm text-slate-600">
                        <div className="truncate">
                          {a.signed_up_via_tenant_id
                            ? <Link to={`/tenants/${a.signed_up_via_tenant_id}`} className="font-medium text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{a.signed_up_via_tenant_name}</Link>
                            : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3"><Chip tone={STATUS[a.status].tone} dot>{STATUS[a.status].label}</Chip></td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{ago(a.last_login_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{date(a.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && <AccountPanel id={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>
  );
}

function AccountPanel({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const { canWrite } = useAccess();
  const { data: d, error, loading, reload } = useApi<Detail>(`/portal-accounts/${id}`);
  const [confirm, setConfirm] = useState<null | 'block' | 'delete'>(null);
  const [busy, setBusy] = useState(false);

  const act = async (fn: () => Promise<unknown>, msg: string, close = false) => {
    setBusy(true);
    try { await fn(); toast('ok', msg); onChanged(); if (close) onClose(); else reload(); setConfirm(null); }
    catch (e) { toast('error', errorText(e)); } finally { setBusy(false); }
  };

  const a = d?.account;
  const g = grade(a?.rating?.score ?? null);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/20" onClick={onClose}>
      <aside className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-slate-900">{a ? a.name || a.email : 'Portal account'}</div>
            {a && <div className="truncate text-sm text-slate-500">{a.email}</div>}
          </div>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {loading ? <div className="text-sm text-slate-500">Loading…</div> : error || !d || !a ? <ErrorBanner text={error || 'Not found'} onRetry={reload} /> : (<>
            <div className="flex flex-wrap gap-1.5">
              <Chip tone={STATUS[a.status].tone} dot>{STATUS[a.status].label}</Chip>
            </div>
            {a.status === 'blocked' && a.blocked_reason && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">Blocked: {a.blocked_reason}</div>}

            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-xs text-slate-500">Company domain</dt><dd className="font-medium text-slate-900">{a.company_domain || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Security rating</dt><dd className="font-medium text-slate-900">
                {a.rating?.score != null ? <>{a.rating.score} · Grade {g} <span className="font-normal text-slate-500">({date(a.rating.scanned_at)})</span></> : 'Not rated yet'}</dd></div>
              <div><dt className="text-xs text-slate-500">Joined</dt><dd className="text-slate-900">{date(a.created_at)}</dd></div>
              <div><dt className="text-xs text-slate-500">Last sign-in</dt><dd className="text-slate-900">{ago(a.last_login_at)}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-slate-500">Workspace</dt><dd className="text-slate-900">
                {a.claimed_tenant_id ? <Link to={`/tenants/${a.claimed_tenant_id}`} className="text-blue-600 hover:underline">{a.claimed_tenant_name || 'Open workspace'}</Link> : '—'}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-slate-500">Signed up via</dt><dd className="text-slate-900">
                {a.signed_up_via_tenant_id
                  ? <>The invite from <Link to={`/tenants/${a.signed_up_via_tenant_id}`} className="font-medium text-blue-600 hover:underline">{a.signed_up_via_tenant_name}</Link> brought them here.</>
                  : 'Unknown (no recorded source tenant).'}</dd></div>
            </dl>

            <section>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Also invited by</div>
              {d.invited_by.length === 0 ? <div className="text-sm text-slate-500">No open invitations.</div> : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {d.invited_by.map((i, k) => (
                    <li key={k} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <Link to={`/tenants/${i.tenant_id}`} className="font-medium text-blue-600 hover:underline">{i.company_name}</Link>
                      <span className="truncate text-slate-500">as vendor {i.vendor_name || '—'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Questionnaires ({d.questionnaires.length}) · Remediation ({d.remediation.length})</div>
              <ul className="space-y-1.5 text-sm">
                {d.questionnaires.slice(0, 8).map(q => (
                  <li key={q.id} className="flex justify-between gap-3"><span className="truncate text-slate-700">{q.questionnaire || 'Questionnaire'} · {q.tenant}</span><span className="shrink-0 text-xs text-slate-500">{q.status} · {date(q.sent_at)}</span></li>
                ))}
                {d.remediation.slice(0, 5).map(r => (
                  <li key={r.id} className="flex justify-between gap-3"><span className="truncate text-slate-700">{r.title} · {r.tenant}</span><span className="shrink-0 text-xs text-slate-500">{r.status.replace('_', ' ')}</span></li>
                ))}
                {d.questionnaires.length + d.remediation.length === 0 && <li className="text-slate-500">None.</li>}
              </ul>
            </section>

            {d.history.length > 0 && (
              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Admin history</div>
                <ul className="space-y-1.5 text-sm">
                  {d.history.map((h, k) => <li key={k} className="text-slate-600"><b className="font-medium text-slate-800">{actionLabel(h.action)}</b> · {h.actor || 'FluxWatch team'} · {ago(h.created_at)}</li>)}
                </ul>
              </section>
            )}
          </>)}
        </div>

        {a && canWrite && (
          <div className="flex flex-wrap gap-2 border-t border-slate-100 px-5 py-3">
            {a.status === 'blocked'
              ? <Button icon={ShieldCheck} loading={busy} onClick={() => act(() => api.post(`/portal-accounts/${id}/unblock`), 'Account unblocked')}>Unblock</Button>
              : <Button icon={Ban} onClick={() => setConfirm('block')}>Block</Button>}
            <Button icon={LogOut} loading={busy} onClick={() => act(() => api.post(`/portal-accounts/${id}/sign-out`), 'Signed out everywhere')}>Sign out everywhere</Button>
            <Button icon={Trash2} variant="danger" className="ml-auto" onClick={() => setConfirm('delete')}>Delete</Button>
          </div>
        )}
      </aside>
      {confirm && a && <ReasonModal kind={confirm} email={a.email} busy={busy} onClose={() => setConfirm(null)}
        onConfirm={reason => confirm === 'block'
          ? act(() => api.post(`/portal-accounts/${id}/block`, { reason }), 'Account blocked')
          : act(() => api.delete(`/portal-accounts/${id}`, { data: { reason } }), 'Account deleted', true)} />}
    </div>
  );
}

function ReasonModal({ kind, email, busy, onClose, onConfirm }: { kind: 'block' | 'delete'; email: string; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal open title={kind === 'block' ? `Block ${email}` : `Delete ${email}`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" loading={busy} disabled={reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>{kind === 'block' ? 'Block' : 'Delete'}</Button></>}>
      <p className="mb-4 text-sm text-slate-600">
        {kind === 'block'
          ? "They're signed out now and can't sign in to the vendor portal until you unblock them. Their answers stay with the customer."
          : 'Removes their sign-in. Answers, evidence and messages they already sent stay with the customer. If they are invited again they can register again.'}
      </p>
      <Field label="Reason (kept in the audit log)"><Input value={reason} onChange={e => setReason(e.target.value)} placeholder={kind === 'block' ? 'Abusive uploads' : 'Requested by the person'} autoFocus /></Field>
    </Modal>
  );
}
